import { ReportResponse } from '../report';
import { V3Person } from './people';

export interface SubscriptionInvoiceReport {
  schoolId: number | null;
  schoolName: string;
  newStudentRegistrations: number;
  renewedStudentRegistrations: number;
  newTeacherRegistrations: number;
  renewedTeacherRegistrations: number;
  newTeacherSubscriptions: number;
  renewedTeacherSubscriptions: number;
  manualRenewedStudentRegistrations: number;
  automaticRenewedStudentRegistrations: number;
  payableStudentRegistrations: number;
  manualRenewedTeacherRegistrations: number;
  automaticRenewedTeacherRegistrations: number;
  payableTeacherRegistrations: number;
  manualRenewedTeacherSubscriptions: number;
  automaticRenewedTeacherSubscriptions: number;
  payableTeacherSubscriptions: number;
  payableStudents: number;
  payableTeachers: number;
  manualBillableEvents: number;
  automaticRenewals: number;
  stoppedSubscriptions: number;
  expiredSubscriptions: number;
  totalPayable: number;
}

/**
 * Maps generic invoice-report rows to typed view models. Column labels are
 * matched case-insensitively because H2 uppercases SQL aliases while MySQL
 * preserves them.
 */
export function invoiceReportsFromGeneric(
  report: ReportResponse | null,
): SubscriptionInvoiceReport[] {
  if (!report?.data?.length) {
    return [];
  }
  const index = new Map(
    report.colsMeta.map((col, i) => [col.columnLabel.toLowerCase(), i]),
  );
  const num = (row: unknown[], column: string): number =>
    Number(row[index.get(column.toLowerCase())] ?? 0);
  return report.data.map((row) => ({
    schoolId: num(row, 'schoolId'),
    schoolName: String(row[index.get('schoolname')] ?? ''),
    newStudentRegistrations: num(row, 'newStudentRegistrations'),
    renewedStudentRegistrations: num(row, 'renewedStudentRegistrations'),
    newTeacherRegistrations: num(row, 'newTeacherRegistrations'),
    renewedTeacherRegistrations: num(row, 'renewedTeacherRegistrations'),
    newTeacherSubscriptions: num(row, 'newTeacherSubscriptions'),
    renewedTeacherSubscriptions: num(row, 'renewedTeacherSubscriptions'),
    manualRenewedStudentRegistrations: num(row, 'manualRenewedStudentRegistrations'),
    automaticRenewedStudentRegistrations: num(row, 'automaticRenewedStudentRegistrations'),
    payableStudentRegistrations: num(row, 'payableStudentRegistrations'),
    manualRenewedTeacherRegistrations: num(row, 'manualRenewedTeacherRegistrations'),
    automaticRenewedTeacherRegistrations: num(row, 'automaticRenewedTeacherRegistrations'),
    payableTeacherRegistrations: num(row, 'payableTeacherRegistrations'),
    manualRenewedTeacherSubscriptions: num(row, 'manualRenewedTeacherSubscriptions'),
    automaticRenewedTeacherSubscriptions: num(row, 'automaticRenewedTeacherSubscriptions'),
    payableTeacherSubscriptions: num(row, 'payableTeacherSubscriptions'),
    payableStudents: num(row, 'payableStudents'),
    payableTeachers: num(row, 'payableTeachers'),
    manualBillableEvents: num(row, 'manualBillableEvents'),
    automaticRenewals: num(row, 'automaticRenewals'),
    stoppedSubscriptions: num(row, 'stoppedSubscriptions'),
    expiredSubscriptions: num(row, 'expiredSubscriptions'),
    totalPayable: num(row, 'totalPayable'),
  }));
}

export enum SubscriptionProductType {
  StudentRegistration = 'STUDENT_REGISTRATION',
  TeacherRegistration = 'TEACHER_REGISTRATION',
  TeacherSubscription = 'TEACHER_SUBSCRIPTION',
}

export enum SubscriptionEventType {
  Activation = 'ACTIVATION',
  Renewal = 'RENEWAL',
  Cancellation = 'CANCELLATION',
  Update = 'UPDATE',
}

export enum SubscriptionEventSource {
  Manual = 'MANUAL',
  Automatic = 'AUTOMATIC',
}

const EVENT_BADGE_CLASSES: Record<SubscriptionEventType, string> = {
  [SubscriptionEventType.Activation]: 'bg-success',
  [SubscriptionEventType.Renewal]: 'bg-primary',
  [SubscriptionEventType.Cancellation]: 'bg-secondary',
  [SubscriptionEventType.Update]: 'bg-info',
};

export function subscriptionEventBadgeClass(
  eventType: SubscriptionEventType,
): string {
  return EVENT_BADGE_CLASSES[eventType] ?? 'bg-secondary';
}

/**
 * Display label of an audit event; renewals spell out their source
 * ("Auto renewal" / "Manual renewal"), other events keep the raw type.
 */
export function subscriptionEventLabel(event: {
  eventType: SubscriptionEventType;
  eventSource: SubscriptionEventSource;
}): string {
  if (event.eventType === SubscriptionEventType.Renewal) {
    return event.eventSource === SubscriptionEventSource.Automatic
      ? 'Auto renewal'
      : 'Manual renewal';
  }
  return event.eventType;
}

const PRODUCT_TYPE_LABELS: Record<SubscriptionProductType, string> = {
  [SubscriptionProductType.StudentRegistration]: 'Student registration',
  [SubscriptionProductType.TeacherRegistration]: 'Teacher registration',
  [SubscriptionProductType.TeacherSubscription]: 'Teacher subscription',
};

export function productTypeLabel(productType: SubscriptionProductType): string {
  return PRODUCT_TYPE_LABELS[productType] ?? productType;
}

export enum SubscriptionStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Cancelled = 'CANCELLED',
}

export interface SubscriptionConfig {
  schoolId: number | null;
  autoCreateStudentRegistration: boolean;
  autoCreateTeacherRegistration: boolean;
  teacherSubscriptionEnabled: boolean;
  autoRenewEnabled: boolean;
  /**
   * Default renewal rule (total paid months from activation) stamped on newly
   * created subscriptions of each product type; null = renew until manually
   * stopped. Changing a default never touches existing subscriptions.
   */
  studentRegistrationDefaultMonths: number | null;
  teacherRegistrationDefaultMonths: number | null;
  teacherSubscriptionDefaultMonths: number | null;
}

/**
 * Renewal rule of a subscription, shared by the read model and the PATCH
 * payload. At most one of renewalMonthsLimit (total paid months from the
 * activation anchor, first month included) and renewUntilDate (last day,
 * inclusive, a renewal may be generated) may be set; both absent = renew
 * until manually stopped.
 */
export interface SubscriptionRenewalRule {
  autoRenew: boolean;
  renewalMonthsLimit?: number | null;
  renewUntilDate?: string | null;
}

export interface Subscription {
  id: number;
  schoolId: number;
  person: V3Person;
  /** School membership id (student/teacher record); null when the member was removed. */
  memberId?: number;
  productType: SubscriptionProductType;
  status: SubscriptionStatus;
  renewal: SubscriptionRenewalRule;
  /**
   * Renewals were deliberately stopped (manually or by reaching the rule
   * limit); the subscription is not reopened automatically while the member
   * stays active.
   */
  renewalStopped: boolean;
  startDate: string;
  expiryDate: string;
  renewalCount: number;
  created: string;
  modified: string;
}

/**
 * PATCH payload. A missing renewal leaves the rule untouched (notes-only
 * update); a present one replaces the rule as a whole, so limits omitted
 * inside it are cleared.
 */
export interface SubscriptionUpdateRequest {
  renewal?: SubscriptionRenewalRule;
  notes?: string;
}


export interface SubscriptionEvent {
  id: number;
  subscriptionId: number;
  schoolId: number;
  schoolName: string;
  person: V3Person;
  productType: SubscriptionProductType;
  eventType: SubscriptionEventType;
  eventSource: SubscriptionEventSource;
  previousExpiryDate: string | null;
  newExpiryDate: string | null;
  eventDate: string;
  effectiveDate: string;
  billingMonth: string;
  notes: string | null;
  subscriptionStatus: string;
}

